// 2 nov 2026 (Augusto: "¿se puede cambiar de WhatsApp a Telegram?" ->
// "hagamos eso"; y "PCM pueda automáticamente leer documentos puestos en un
// folder en el folder PCM en el drive (Por procesar) y una vez terminado
// mandarlo a Procesado").
//
// Un solo lugar para conectar los canales por los que entra información a
// PCM sin abrir la app: el bot de Telegram y la carpeta de Google Drive.
import React, { useCallback, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TelegramIcon from '@mui/icons-material/Telegram';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const mensajeDeError = (err, isSpanish) =>
  err?.response?.data?.detail ||
  (isSpanish ? 'No se pudo completar. Revisa tu conexión e inténtalo de nuevo.' : 'Could not complete.');

const Paso = ({ n, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
    <Box
      sx={{
        minWidth: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.200',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700,
      }}
    >
      {n}
    </Box>
    <Box sx={{ flex: 1, pt: 0.25 }}>{children}</Box>
  </Box>
);

const TelegramCard = ({ isSpanish }) => {
  const [estado, setEstado] = useState(null);
  const [codigo, setCodigo] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(() => {
    apiClient.get('/telegram/estado').then((r) => setEstado(r.data)).catch(() => setEstado({ activo: false }));
  }, []);
  useEffect(cargar, [cargar]);

  // Mientras hay un código en pantalla, se revisa cada 5 s si ya se vinculó.
  useEffect(() => {
    if (!codigo || estado?.vinculado) return undefined;
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, [codigo, estado?.vinculado, cargar]);

  const generar = async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/telegram/codigo');
      setCodigo(data.codigo);
    } catch (err) {
      setError(mensajeDeError(err, isSpanish));
    } finally {
      setCargando(false);
    }
  };
  const desvincular = async () => {
    if (!window.confirm(isSpanish ? '¿Desconectar Telegram de tu cuenta de PCM?' : 'Disconnect Telegram?')) return;
    try {
      await apiClient.delete('/telegram/vinculo');
      setCodigo(null);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err, isSpanish));
    }
  };

  const enlaceBot = estado?.bot ? `https://t.me/${estado.bot}` : null;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <TelegramIcon sx={{ color: '#229ED9' }} />
        <Typography variant="h6">Telegram</Typography>
        {estado?.vinculado && <Chip size="small" color="success" label={isSpanish ? 'Vinculado' : 'Linked'} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isSpanish
          ? 'Registra movimientos escribiendo o mandando la foto del recibo, envía estados de cuenta en PDF o Excel, hazle preguntas, y recibe los avisos de PCM (día de pago de tarjetas con botones para confirmar, presupuestos) directo en el chat.'
          : 'Record transactions, send statements, ask questions, and get PCM alerts with buttons right in the chat.'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {estado && !estado.activo && (
        <Alert severity="info">
          {isSpanish
            ? 'El bot de Telegram todavía no está activado en el servidor de PCM.'
            : 'The Telegram bot is not active on the server yet.'}
        </Alert>
      )}

      {estado?.activo && estado.vinculado && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {isSpanish ? 'Tu chat está conectado' : 'Your chat is connected'}
            {enlaceBot && (
              <>
                {' · '}
                <a href={enlaceBot} target="_blank" rel="noreferrer">@{estado.bot}</a>
              </>
            )}
          </Typography>
          <Button size="small" color="error" onClick={desvincular}>
            {isSpanish ? 'Desconectar' : 'Disconnect'}
          </Button>
        </Box>
      )}

      {estado?.activo && !estado.vinculado && (
        <>
          <Paso n={1}>
            <Typography variant="body2">
              {isSpanish ? 'Abre el bot de PCM en Telegram' : 'Open the PCM bot in Telegram'}
              {enlaceBot ? (
                <>
                  {': '}
                  <a href={enlaceBot} target="_blank" rel="noreferrer">@{estado.bot}</a>
                </>
              ) : '.'}
            </Typography>
          </Paso>
          <Paso n={2}>
            {codigo ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {isSpanish ? 'Mándale este mensaje (vence en 15 minutos):' : 'Send it this message (expires in 15 min):'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '1.2rem', bgcolor: 'grey.100', px: 1.5, py: 0.5, borderRadius: 1 }}>
                    /vincular {codigo}
                  </Typography>
                  <Tooltip title={isSpanish ? 'Copiar' : 'Copy'}>
                    <IconButton size="small" onClick={() => navigator.clipboard?.writeText(`/vincular ${codigo}`)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Esta pantalla se actualiza sola cuando quede vinculado.' : 'This screen updates itself once linked.'}
                </Typography>
              </Box>
            ) : (
              <Button variant="contained" size="small" disabled={cargando} onClick={generar}>
                {isSpanish ? 'Generar mi código' : 'Generate my code'}
              </Button>
            )}
          </Paso>
        </>
      )}
    </Paper>
  );
};

const ESTADO_CHIP = {
  procesado: { color: 'success', es: 'Importado', en: 'Imported' },
  revisar: { color: 'warning', es: 'En Revisar', en: 'To review' },
};

const DriveCard = ({ isSpanish }) => {
  const [estado, setEstado] = useState(null);
  const [enlace, setEnlace] = useState('');
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    apiClient.get('/drive').then((r) => setEstado(r.data)).catch(() => setEstado({ disponible: false }));
  }, []);
  useEffect(cargar, [cargar]);

  const conectar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/drive/carpeta', { enlace });
      setEstado(data);
      setEnlace('');
      setAviso(isSpanish
        ? 'Listo. Dentro de tu carpeta ya están "Por procesar", "Procesado" y "Revisar".'
        : 'Done. Your folder now has the three subfolders.');
    } catch (err) {
      setError(mensajeDeError(err, isSpanish));
    } finally {
      setGuardando(false);
    }
  };
  const desconectar = async () => {
    if (!window.confirm(isSpanish
      ? '¿Desconectar la carpeta? PCM dejará de revisarla. No se borra nada de tu Drive.'
      : 'Disconnect the folder?')) return;
    try {
      setEstado((await apiClient.delete('/drive/carpeta')).data);
    } catch (err) {
      setError(mensajeDeError(err, isSpanish));
    }
  };
  const revisarAhora = async () => {
    setError(null);
    try {
      await apiClient.post('/drive/revisar');
      setAviso(isSpanish
        ? 'Revisando la carpeta. Un estado de cuenta puede tardar un minuto; te aviso por Telegram o correo, y abajo aparece el resultado.'
        : 'Checking the folder now.');
      setTimeout(cargar, 30000);
      setTimeout(cargar, 90000);
    } catch (err) {
      setError(mensajeDeError(err, isSpanish));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <FolderSharedIcon sx={{ color: '#1A73E8' }} />
        <Typography variant="h6">Google Drive</Typography>
        {estado?.carpeta && <Chip size="small" color="success" label={isSpanish ? 'Conectado' : 'Connected'} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isSpanish
          ? `Deja tus estados de cuenta (PDF), fotos de recibos o Excel en la carpeta "Por procesar". PCM la revisa cada ${estado?.intervalo_minutos || 10} minutos: lo que importa bien pasa a "Procesado"; lo que deja dudas NO se importa y pasa a "Revisar", con el motivo en la descripción del archivo.`
          : 'Drop statements, receipts or spreadsheets in "Por procesar"; PCM imports them and files them away.'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)}>{aviso}</Alert>}

      {estado && !estado.disponible && (
        <Alert severity="info">
          {isSpanish
            ? 'La conexión con Google Drive todavía no está activada en el servidor de PCM.'
            : 'Google Drive is not active on the server yet.'}
        </Alert>
      )}

      {estado?.disponible && estado.carpeta && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="body2">
              {isSpanish ? 'Carpeta: ' : 'Folder: '}
              <a href={estado.carpeta.enlace} target="_blank" rel="noreferrer">{estado.carpeta.nombre}</a>
            </Typography>
            <Button variant="contained" size="small" onClick={revisarAhora}>
              {isSpanish ? 'Revisar ahora' : 'Check now'}
            </Button>
            <Button size="small" color="error" onClick={desconectar}>
              {isSpanish ? 'Desconectar' : 'Disconnect'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isSpanish
              ? 'Para Excel o CSV, ponle al archivo el nombre de la cuenta (por ejemplo "BCP Soles octubre.xlsx"); igual con una foto de recibo si tienes varias tarjetas.'
              : 'Name spreadsheets after the account they belong to.'}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{isSpanish ? 'Últimos archivos' : 'Recent files'}</Typography>
          {(estado.historial || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Todavía no procesé ningún archivo.' : 'No files yet.'}
            </Typography>
          ) : (
            estado.historial.map((h) => {
              const chip = ESTADO_CHIP[h.estado] || {};
              return (
                <Box key={h.id} sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}>
                  <Chip size="small" color={chip.color || 'default'} label={isSpanish ? chip.es : chip.en} sx={{ mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{h.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {h.resumen}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {h.fecha ? new Date(h.fecha).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </Typography>
                </Box>
              );
            })
          )}
        </>
      )}

      {estado?.disponible && !estado.carpeta && (
        <>
          <Paso n={1}>
            <Typography variant="body2">
              {isSpanish ? 'En tu Google Drive, crea una carpeta llamada "PCM" (o usa la que ya tienes).' : 'Create a "PCM" folder in your Drive.'}
            </Typography>
          </Paso>
          <Paso n={2}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {isSpanish ? 'Compártela como ' : 'Share it as '}<b>{isSpanish ? 'Editor' : 'Editor'}</b>
              {isSpanish ? ' con este correo:' : ' with this email:'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1, wordBreak: 'break-all' }}>
                {estado.correo_pcm}
              </Typography>
              <Tooltip title={isSpanish ? 'Copiar' : 'Copy'}>
                <IconButton size="small" onClick={() => navigator.clipboard?.writeText(estado.correo_pcm || '')}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {isSpanish ? 'PCM solo ve esa carpeta, nunca el resto de tu Drive.' : 'PCM only sees that folder.'}
            </Typography>
          </Paso>
          <Paso n={3}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {isSpanish ? 'Pega acá el enlace de la carpeta (Compartir → Copiar enlace):' : 'Paste the folder link:'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                fullWidth
                sx={{ maxWidth: 520 }}
                placeholder="https://drive.google.com/drive/folders/…"
                value={enlace}
                onChange={(e) => setEnlace(e.target.value)}
              />
              <Button variant="contained" size="small" disabled={!enlace.trim() || guardando} onClick={conectar}>
                {guardando ? (isSpanish ? 'Conectando…' : 'Connecting…') : (isSpanish ? 'Conectar' : 'Connect')}
              </Button>
            </Box>
          </Paso>
        </>
      )}
    </Paper>
  );
};

const ConexionesPage = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Conexiones' : 'Connections'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish
          ? 'Canales por los que entra información a PCM sin abrir la app.'
          : 'Channels that feed PCM without opening the app.'}
      </Typography>
      <TelegramCard isSpanish={isSpanish} />
      <DriveCard isSpanish={isSpanish} />
    </Container>
  );
};

export default ConexionesPage;
